// Putting a different course on this device.
//
// The other half of MYN-104's corpus-store.ts, which knew how to HOLD a corpus and had nothing to
// put in it. This fetches one from the catalogue MYN-108 generates and hands it over.
//
// IT IS A DOWNLOAD AND A DELETE, IN THAT ORDER, AND THE ORDER MATTERS. The delete is the whole
// point of "one language at a time" — 189 MB of recordings, not the 1.4 MB of text — and doing it
// first would leave somebody with nothing at all if the network went away mid-swap. So: fetch
// everything into memory, and only when all of it has arrived replace what is there. A corpus is
// a megabyte and a half; holding it for a few seconds costs nothing and makes the swap atomic
// from the learner's point of view.
//
// NOT THROUGH api(). The same reason clip-cache.ts is not: this is a long, cancellable transfer
// with a progress bar, and the API seam answers questions rather than running jobs.

import { listingById, type Catalogue, type PairListing } from '../src/shared/catalogue.js';
import * as corpusStore from './corpus-store.js';

/** How far a swap has got. Bytes, because that is what a person is waiting for. */
export interface InstallProgress {
  /** The file being fetched, for a line that says something is happening. */
  file: string;
  done: number;
  total: number;
}

export type Trouble = 'unreachable' | 'refused' | null;

let running = false;
let cancelled = false;

export const installing = (): boolean => running;

/**
 * What there is to learn.
 *
 * The bundled copy first, because it is there and it is instant. Then the origin's, because a
 * build from six months ago knows nothing about a pair added since — and this is the only way a
 * phone that has already shipped ever hears about one.
 *
 * The remote copy is a bonus, never a requirement: no network means the list the build came with,
 * which always contains at least the pair it is running.
 */
export async function loadCatalogue(): Promise<Catalogue | null> {
  const local = await fetch('catalogue.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? (r.json() as Promise<Catalogue>) : null))
    .catch(() => null);
  const origin = local?.origin;
  if (!origin) return local;
  const remote = await fetch(`${origin}/catalogue.json`, { cache: 'no-cache' })
    .then((r) => (r.ok ? (r.json() as Promise<Catalogue>) : null))
    .catch(() => null);
  // A remote list with fewer pairs than the bundle is a deployment that is BEHIND this app, not a
  // reason to forget the course it is running.
  return remote && remote.pairs.length >= local.pairs.length ? remote : local;
}

/** Stop the transfer in flight. Nothing is replaced, so there is nothing to undo. */
export function cancel(): void {
  cancelled = true;
}

/**
 * Fetch a course and put it on this device, replacing whatever was there.
 *
 * Throws with something a person can read. The caller reloads on success: every view, the
 * catalogues, the title and the document's language are all chosen from the corpus at boot
 * (MYN-107), and rebuilding that in place would be a second boot path to keep working.
 */
export async function install(
  catalogue: Catalogue,
  id: string,
  onProgress: (p: InstallProgress) => void,
): Promise<void> {
  const listing = listingById(catalogue, id);
  if (!listing) throw new Error('that course is not in the catalogue');
  if (running) throw new Error('a course is already being installed');
  running = true;
  cancelled = false;
  try {
    const total = listing.corpus.files.reduce((sum, f) => sum + f.bytes, 0);
    const fetched: Record<string, string> = {};
    let done = 0;
    for (const file of listing.corpus.files) {
      if (cancelled) throw new Error('cancelled');
      onProgress({ file: file.name, done, total });
      const url = `${catalogue.origin}/${listing.corpus.prefix}${file.name}`;
      let res: Response;
      try {
        res = await fetch(url, { cache: 'no-cache' });
      } catch {
        // Includes the case that bit MYN-94: a cross-origin response with no
        // access-control-allow-origin is discarded by the browser before the app sees it, and it
        // arrives here indistinguishable from being offline.
        throw new Error(`Could not reach ${catalogue.origin}. The download will need a connection.`);
      }
      if (!res.ok) throw new Error(`${file.name} is not on the server (${res.status})`);
      const text = await res.text();
      // The catalogue said how big this is. A body that does not match it is a truncated transfer
      // or a proxy's error page, and writing either one would install a corpus that cannot parse.
      if (new TextEncoder().encode(text).length !== file.bytes) {
        throw new Error(`${file.name} arrived the wrong size — the download was interrupted.`);
      }
      fetched[file.name] = text;
      done += file.bytes;
      onProgress({ file: file.name, done, total });
    }
    if (cancelled) throw new Error('cancelled');
    // Only now. Everything above this line can fail without costing anybody the course they had.
    await corpusStore.install(fetched);
  } finally {
    running = false;
  }
}

/** Go back to the corpus the app shipped with. Nothing is downloaded; the files are already there. */
export async function useBundled(): Promise<void> {
  await corpusStore.remove();
}

/** Which course is on the device, by its catalogue id, or null for the one that shipped. */
export async function installedId(catalogue: Catalogue | null): Promise<string | null> {
  const key = await corpusStore.installed();
  if (!key || !catalogue) return null;
  // corpus-store answers in the pair's own terms — "en/zh-CN" — and the catalogue is keyed by
  // directory. Matching on what the pair DECLARES is the rule shared/item-key.ts sets.
  const [learning, explainedIn] = key.split('/');
  return (
    catalogue.pairs.find(
      (p) => p.learning.code === learning && p.explainedIn.code === explainedIn,
    )?.id ?? null
  );
}

export type { PairListing };
